<template>
  <BaseContainer>
    <BaseFlexSpinner v-if="state.isLoading" />
    <BaseCard class="mx-5 mt-3 mb-24">
      <div v-if="state.loadFailed" class="flex flex-col items-center gap-3 py-10 text-center">
        <p class="text-base font-semibold text-gray-dark">
          La liste des patients n'a pas pu être chargée
        </p>
        <p class="text-sm text-slate-500">
          Vérifiez votre connexion et réessayez.
        </p>
        <Button variant="outline" size="sm" class="border-solid" @click="loadConsumers">
          <RotateCw />
          Réessayer
        </Button>
      </div>
      <MedicineCabinetConsumersTable
        v-else
        :consumers="state.consumers"
        @consumer-click="showCustomer"
      />
    </BaseCard>
  </BaseContainer>
</template>

<script lang="ts" setup>
import {onMounted, reactive} from "vue";
import BaseContainer from '@/core/components/base/BaseContainer.vue';
import {medicineCabinetApi} from '@/container';
import BaseCard from '@/core/components/base/BaseCard.vue';
import BaseFlexSpinner from '@/core/components/base/spinner/BaseFlexSpinner.vue';
import MedicineCabinetConsumersTable from '@/program/components/program-consumers/MedicineCabinetConsumersTable.vue';
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";
import {Program} from "@/program/interfaces/Program";
import Consumer from "@/core/interfaces/consumers/Consumer";
import {useRouter} from "vue-router";
import {RotateCw} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";

const router = useRouter();

defineProps<{
    program: Program
}>();

const state = reactive({
    isLoading: false,
    loadFailed: false,
    consumers: [] as Consumer[],
});

const systemStore = useSystemStore();
const toastStore = useToastStore();

/**
 * Show the customer details.
 *
 * @param consumer
 */
const showCustomer = (consumer: Consumer) => {
    router.push({
        name: 'customer.home',
        params: {
            customerId: consumer.id
        }
    });
};

/**
 * Load the consumers. A failure (timeout, 5xx, network) swaps the table for
 * a retry state instead of leaving the spinner up.
 */
const loadConsumers = async (): Promise<void> => {
    if (!systemStore.environment?.entityId) {
        return;
    }

    state.isLoading = true;
    state.loadFailed = false;

    try {
        state.consumers = await medicineCabinetApi().consumersStatistics(systemStore.environment.entityId);
    } catch {
        state.loadFailed = true;
        toastStore.showError({
            content: 'Impossible de charger les patients. Vérifiez votre connexion et réessayez.'
        });
    } finally {
        state.isLoading = false;
    }
};

/**
 * On mounting, load the consumers.
 */
onMounted(loadConsumers);
</script>
